import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";

export default function PayPalReturn() {
  const loc = useLocation();
  const nav = useNavigate();
  const [status, setStatus] = useState("capturing"); // capturing | success | failed
  const [amount, setAmount] = useState(null);
  const [purpose, setPurpose] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const orderId = params.get("token"); // PayPal returns ?token=ORDER_ID&PayerID=...
    const txnId = params.get("txn");
    const purposeParam = params.get("purpose") || "";
    const slug = params.get("slug") || "";
    setPurpose(purposeParam);

    if (!orderId) {
      setStatus("failed");
      toast.error("Missing PayPal order id. If you were charged, please contact us.");
      return;
    }

    apiClient.post("/paypal/checkout/capture", { order_id: orderId, txn_id: txnId }, { timeout: 30000 })
      .then(({ data }) => {
        const txn = data?.txn || {};
        const s = data?.status || txn?.status || "";
        if (data?.already_captured || s === "COMPLETED" || txn.payment_status === "paid") {
          setAmount(txn.amount_captured_usd || txn.amount_usd);
          setStatus("success");
          toast.success("Payment received ✦ Thank you.");
          // Land buyers where it makes most sense
          if (purposeParam === "pdf" && slug) {
            setTimeout(() => nav(`/memorial/${slug}?pdf_unlocked=1`, { replace: true }), 3200);
          } else if (purposeParam === "upgrade" && slug) {
            setTimeout(() => nav(`/memorial/${slug}?upgraded=1`, { replace: true }), 3200);
          } else {
            setTimeout(() => nav("/gallery", { replace: true }), 3200);
          }
        } else {
          setStatus("failed");
          toast.error(`Payment status: ${s}. Please contact us.`);
        }
      })
      .catch((e) => {
        console.error(e);
        setStatus("failed");
        toast.error(e?.response?.data?.detail || "Could not confirm the payment.");
      });
  }, [loc.search, nav]);

  return (
    <main className="relative max-w-2xl mx-auto px-6 py-24 text-center" data-testid="paypal-return-page">
      <div className="hero-card rounded-sm px-10 py-16">
        {status === "capturing" && (
          <>
            <div className="tracking-celestial text-[var(--rb-gold)] mb-4">One moment</div>
            <h1 className="font-serif-display text-3xl text-[var(--rb-text-heading)] mb-6">Confirming your PayPal payment…</h1>
            <p className="font-serif-display italic text-[var(--rb-text-body)]">Please do not close this window.</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="tracking-celestial text-[var(--rb-gold)] mb-4">Thank you, kindly.</div>
            <h1 className="font-serif-display text-4xl text-[var(--rb-text-heading)] mb-6">
              {purpose === "donation" ? "Your gift is received ✦" : purpose === "pdf" ? "Your keepsake is unlocked ✦" : purpose === "upgrade" ? "Memorial upgraded ✦" : "Payment received ✦"}
            </h1>
            <p className="font-serif-display italic text-[18px] text-[var(--rb-text-body)] leading-relaxed">
              {amount ? `$${Number(amount).toFixed(2)} — ` : ""}
              {purpose === "donation"
                ? "your generosity helps this garden keep growing."
                : purpose === "deposit"
                ? "we'll reach out shortly to shape the diorama that honors them."
                : purpose === "pdf"
                ? "returning you to the memorial so you can download your printable keepsake."
                : purpose === "upgrade"
                ? "this memorial now lives here forever. Taking you back so you can share it."
                : "your payment has been received with care."}
            </p>
            <p className="text-[12px] text-[var(--rb-text-muted)] italic mt-8">{purpose === "pdf" || purpose === "upgrade" ? "Taking you back to their page…" : "Taking you to the Garden of Remembrance…"}</p>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="tracking-celestial text-red-500 mb-4">Something went wrong</div>
            <h1 className="font-serif-display text-3xl text-[var(--rb-text-heading)] mb-6">We couldn&apos;t confirm your payment.</h1>
            <p className="font-serif-display italic text-[var(--rb-text-body)] mb-6">
              If your card was charged, please reach out and we&apos;ll make it right.
            </p>
            <a href="mailto:Theheirloompet@gmail.com" className="text-[var(--rb-gold)] hover:text-[var(--rb-text-heading)]">Theheirloompet@gmail.com</a>
          </>
        )}
      </div>
    </main>
  );
}
